import React, { Fragment, useRef, useState } from 'react';
import { insuranceNetworkItem } from '../utils/prop-types';
import { useMutation } from '@apollo/client';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import CREATE_OR_UPDATE_INSURANCE_NETWORK from '../mutations/create-or-update-insurance-network.graphql';
import DELETE_INSURANCE_NETWORK from '../mutations/delete-insurance-network.graphql';
import insuranceNetworkSchema from '../utils/insurance-network-schema';
import toastr from '../utils/toastr';
import styles from '../styles.module.scss';

const SERVER_VALIDATION_MESSAGES_DEFAULT = {};
const RECORD_NOT_FOUND = 'This Insurance Network does not exist because it has probably been deleted';

const InsuranceNetworkItem = ({
                                roles,
                                insuranceNetwork,
                                updateInsuranceNetwork,
                                deleteInsuranceNetwork,
                                setShowCreateForm,
                                selectedInsuranceNetwork,
                                setSelectedInsuranceNetwork
                              }) => {
  const [serverValidationMessages, setServerValidationMessages] = useState(SERVER_VALIDATION_MESSAGES_DEFAULT);

  const formikRef = useRef();

  const [callUpdateInsuranceNetwork, { loading: updateLoading }] = useMutation(CREATE_OR_UPDATE_INSURANCE_NETWORK, {
    onCompleted: data => handleUpdateInsuranceNetworkCompleted(data),
    onError: () => handleError()
  });

  const [callDeleteInsuranceNetwork, { loading: deleteLoading }] = useMutation(DELETE_INSURANCE_NETWORK, {
    onCompleted: () => handleDeleteInsuranceNetworkCompleted(),
    onError: () => handleError()
  });

  const handleUpdateInsuranceNetworkCompleted = data => {
    const { success, errors, insuranceNetwork: newInsuranceNetwork } = data.createOrUpdateInsuranceNetwork;
    const { isNotFound } = errors;

    if (success) {
      toastr.success('Updated successfully');
      updateInsuranceNetwork(newInsuranceNetwork);
      setSelectedInsuranceNetwork(null);
      setServerValidationMessages(SERVER_VALIDATION_MESSAGES_DEFAULT);
    } else if (isNotFound) {
      toastr.error(RECORD_NOT_FOUND);
      deleteInsuranceNetwork(insuranceNetwork.id);
    } else {
      toastr.error('Update failed');
      setServerValidationMessages(errors);
    }
  };

  const handleDeleteInsuranceNetworkCompleted = () => {
    toastr.success('Deleted successfully');
    deleteInsuranceNetwork(insuranceNetwork.id);
  };

  const handleError = () => {
    toastr.error('Something went wrong');
  };

  const changeToUpdate = () => {
    setSelectedInsuranceNetwork(insuranceNetwork);
    setShowCreateForm(false);
    setServerValidationMessages(SERVER_VALIDATION_MESSAGES_DEFAULT);
  };

  const handleUpdateInsuranceNetwork = async values => {
    setServerValidationMessages(SERVER_VALIDATION_MESSAGES_DEFAULT);
    const name = values.name.trim();
    if (name !== insuranceNetwork.name) {
      await callUpdateInsuranceNetwork({
        variables: {
          id: insuranceNetwork.id,
          name: name
        }
      });
    } else {
      setSelectedInsuranceNetwork(null);
    }
  };

  const handleDeleteInsuranceNetwork = async () => {
    const isConfirm = confirm('Please confirm you want to delete this Insurance Network?');
    if (isConfirm) {
      await callDeleteInsuranceNetwork({
        variables: {
          id: insuranceNetwork.id
        }
      });
    }
  };

  return (
    <tr>
      <td>{insuranceNetwork.id}</td>
      <td>
        {
          selectedInsuranceNetwork && selectedInsuranceNetwork.id === insuranceNetwork.id ?
            <Formik
              initialValues={insuranceNetwork}
              validationSchema={insuranceNetworkSchema}
              validateOnChange={false}
              validateOnBlur={false}
              enableReinitialize={true}
              innerRef={formikRef}
              onSubmit={values => handleUpdateInsuranceNetwork(values)}
            >
              {({ errors: formikValidationMessages }) => (
                <Form className={styles.form}>
                  <Field
                    className={formikValidationMessages.name || serverValidationMessages.name ? styles.inputError : ''}
                    name='name'
                    type='text'
                    autoFocus={true}
                  />
                  <ErrorMessage
                    className={styles.messageError}
                    component='span'
                    name='name'
                  />
                  {
                    !formikValidationMessages.name && serverValidationMessages.name &&
                    <span className={styles.messageError}>
                      {serverValidationMessages.name}
                    </span>
                  }
                </Form>
              )}
            </Formik>
            :
            insuranceNetwork.name
        }
      </td>
      {
        roles.canModify &&
        <td>
          {
            selectedInsuranceNetwork && selectedInsuranceNetwork.id === insuranceNetwork.id ?
              <Fragment>
                <button
                  className='button mr-1'
                  disabled={updateLoading}
                  onClick={() => formikRef.current.submitForm()}
                >
                  {updateLoading ? 'Saving...' : 'Save'}
                </button>
                <button
                  className='button secondary'
                  onClick={() => setSelectedInsuranceNetwork(null)}
                >
                  Cancel
                </button>
              </Fragment>
              :
              deleteLoading ?
                <span>Deleting...</span>
                :
                <Fragment>
                  <a
                    className={styles.actionButton}
                    onClick={() => changeToUpdate()}
                  >
                    Edit
                  </a>
                  <a
                    className={styles.actionButton}
                    onClick={() => handleDeleteInsuranceNetwork()}
                  >
                    Delete
                  </a>
                </Fragment>
          }
        </td>
      }
    </tr>
  );
};

InsuranceNetworkItem.propTypes = insuranceNetworkItem;

export default InsuranceNetworkItem;
